import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { PtoEquipment } from '@shared/api';
import { Button } from '@shared/ui/Button';
import { Muted, TableWrap } from '../kfo/ui';
import { AiMarkButton } from './AiMark';
import { LoadChartEditor } from './LoadChartEditor';
import {
  EQUIPMENT_KINDS,
  EQ_CHARACTERISTICS_MAX,
  EQ_NAME_MAX,
  MOBILIZATION_METHODS,
  aiFieldDetails,
  editEqField,
  emptyEqRow,
  eqNumError,
  equipmentSummary,
  fillFromRegistry,
  isEqCount,
  pointsWord,
  rowHasLifting,
  textLength,
  verifiedPatch,
  type EqRowDraft,
  type EquipmentAiField,
  type EquipmentNumField,
} from './model';
import { BelowTable, CellInput, CellMark, CellSelect, EqTable, Suggest, SuggestItem } from './ui';

/**
 * Таблица техники мастера — по форме «График мобилизации техники» ППР-004.
 * Наименование подсказывается из справочника организации: выбор подставляет
 * вид, характеристику, г/п, вылет, высоту и ГВХ. Значения из справочных данных
 * ИИ (загрузка файлом) — с кнопкой «ИИ ×»: правка поля или нажатие (значение
 * сверено с паспортом) снимают пометку.
 */
export function EquipmentTable({
  rows,
  onChange,
  registry,
  onImport,
}: {
  rows: EqRowDraft[];
  onChange: (rows: EqRowDraft[]) => void;
  registry: PtoEquipment[];
  /** «Загрузить из файла» под таблицей (перечень техники Excel/CSV). */
  onImport?: () => void;
}) {
  const [chartOpen, setChartOpen] = useState<string | null>(null);
  const byId = useMemo(() => new Map(registry.map((e) => [e.id, e])), [registry]);

  const update = (key: string, patch: Partial<EqRowDraft>) =>
    onChange(rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  /** Правка параметра инженером — значение теперь его, пометка ИИ снимается. */
  const edit = (row: EqRowDraft, field: EquipmentAiField, value: string) =>
    update(row.key, editEqField(row, field, value));
  /**
   * «ИИ ×»: значение сверено с паспортом — пометка снимается, значение остаётся
   * (у характеристики из текста убирается метка «справочно (ИИ):»).
   */
  const clearAi = (row: EqRowDraft, field: EquipmentAiField) =>
    update(row.key, verifiedPatch(row, field));

  function onName(row: EqRowDraft, name: string) {
    // Имя разошлось с выбранной записью справочника — это уже другая техника.
    const linked = row.equipment_id ? byId.get(row.equipment_id) : undefined;
    const keep = linked && linked.name.trim().toLowerCase() === name.trim().toLowerCase();
    update(row.key, { name, equipment_id: keep ? row.equipment_id : null });
  }

  return (
    <div>
      <TableWrap $maxHeight="none">
        <EqTable>
          <thead>
            <tr>
              <th>№</th>
              <th className="c-name">Наименование и марка *</th>
              <th className="c-kind">Вид</th>
              <th className="c-text">Техническая характеристика</th>
              <th className="c-num">Г/п max, т</th>
              <th className="c-num">Вылет max, м</th>
              <th className="c-num">Высота подъёма, м</th>
              <th className="c-count">Кол-во *</th>
              <th className="c-date">Дата мобилизации</th>
              <th className="c-method">Способ мобилизации</th>
              <th className="c-text">Назначение</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const lifting = rowHasLifting(r);
              const chartPoints = r.load_chart.filter((p) => p.reach.trim() || p.cap.trim()).length;
              const aiMark = (field: EquipmentAiField) =>
                r.ai_fields.includes(field) ? (
                  <CellMark>
                    <AiMarkButton
                      details={aiFieldDetails(field)}
                      onClear={() => clearAi(r, field)}
                    />
                  </CellMark>
                ) : null;
              return (
                <Fragment key={r.key}>
                  <tr data-eq-row={i}>
                    <td className="num">{i + 1}</td>
                    <td>
                      <NameInput
                        value={r.name}
                        registry={registry}
                        invalid={textLength(r.name) > EQ_NAME_MAX}
                        onChange={(v) => onName(r, v)}
                        onPick={(e) => update(r.key, fillFromRegistry(r, e))}
                      />
                      {r.equipment_id && <Muted as="small">из справочника</Muted>}
                    </td>
                    <td>
                      <CellSelect
                        value={r.kind}
                        aria-label="Вид техники"
                        onChange={(e) => edit(r, 'kind', e.target.value)}
                      >
                        {EQUIPMENT_KINDS.map(([k, label]) => (
                          <option key={k} value={k}>
                            {label}
                          </option>
                        ))}
                      </CellSelect>
                      {aiMark('kind')}
                    </td>
                    <td>
                      <CellInput
                        value={r.characteristics}
                        aria-label="Техническая характеристика"
                        placeholder={lifting ? 'Грузоподъёмность 25 т' : ''}
                        $invalid={textLength(r.characteristics) > EQ_CHARACTERISTICS_MAX}
                        onChange={(e) => edit(r, 'characteristics', e.target.value)}
                      />
                      {aiMark('characteristics')}
                    </td>
                    {lifting ? (
                      <>
                        <NumCell
                          field="lift_capacity_t"
                          value={r.lift_capacity_t}
                          label="Г/п max, т"
                          mark={aiMark('lift_capacity_t')}
                          onChange={(v) => edit(r, 'lift_capacity_t', v)}
                        />
                        <NumCell
                          field="max_reach_m"
                          value={r.max_reach_m}
                          label="Вылет max, м"
                          mark={aiMark('max_reach_m')}
                          onChange={(v) => edit(r, 'max_reach_m', v)}
                        />
                        <NumCell
                          field="lift_height_m"
                          value={r.lift_height_m}
                          label="Высота подъёма, м"
                          mark={aiMark('lift_height_m')}
                          onChange={(v) => edit(r, 'lift_height_m', v)}
                        />
                      </>
                    ) : (
                      <>
                        <td className="dash">—</td>
                        <td className="dash">—</td>
                        <td className="dash">—</td>
                      </>
                    )}
                    <td>
                      <CellInput
                        value={r.count}
                        inputMode="numeric"
                        aria-label="Количество"
                        $invalid={!isEqCount(r.count)}
                        onChange={(e) => update(r.key, { count: e.target.value })}
                      />
                    </td>
                    <td>
                      <CellInput
                        type="date"
                        value={r.mobilization_date}
                        aria-label="Дата мобилизации"
                        onChange={(e) => update(r.key, { mobilization_date: e.target.value })}
                      />
                    </td>
                    <td>
                      <MethodCell
                        value={r.mobilization_method}
                        onChange={(v) => update(r.key, { mobilization_method: v })}
                      />
                    </td>
                    <td>
                      <CellInput
                        value={r.purpose}
                        aria-label="Назначение"
                        placeholder="монтаж ж/б конструкций"
                        onChange={(e) => update(r.key, { purpose: e.target.value })}
                      />
                    </td>
                    <td className="actions">
                      {lifting && (
                        <Button
                          $variant={chartOpen === r.key ? 'primary' : 'secondary'}
                          $size="sm"
                          type="button"
                          title="Грузовысотная характеристика: вылет → грузоподъёмность"
                          onClick={() => setChartOpen(chartOpen === r.key ? null : r.key)}
                        >
                          ГВХ{chartPoints > 0 ? ` (${chartPoints})` : ''}
                        </Button>
                      )}
                      <Button
                        $variant="ghost"
                        $size="sm"
                        type="button"
                        title="Удалить строку"
                        onClick={() => onChange(rows.filter((x) => x.key !== r.key))}
                      >
                        ✕
                      </Button>
                    </td>
                  </tr>
                  {chartOpen === r.key && (
                    <tr className="sub">
                      <td />
                      <td colSpan={11}>
                        <Muted>
                          ГВХ «{r.name || `строка ${i + 1}`}»
                          {chartPoints > 0 ? ` — ${chartPoints} ${pointsWord(chartPoints)}` : ''}:
                          на каком вылете какую массу кран поднимает.
                        </Muted>
                        <LoadChartEditor
                          points={r.load_chart}
                          onChange={(load_chart) => update(r.key, { load_chart })}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </EqTable>
      </TableWrap>
      <BelowTable>
        <Button
          $variant="secondary"
          $size="sm"
          type="button"
          onClick={() => onChange([...rows, emptyEqRow()])}
        >
          + Добавить технику
        </Button>
        {onImport && (
          <Button
            $variant="secondary"
            $size="sm"
            type="button"
            title="Перечень техники Excel или CSV: строки лягут в таблицу, недостающие характеристики допишет ИИ"
            onClick={onImport}
          >
            Загрузить из файла
          </Button>
        )}
      </BelowTable>
    </div>
  );
}

function NumCell({
  field,
  value,
  label,
  mark,
  onChange,
}: {
  field: EquipmentNumField;
  value: string;
  label: string;
  /** «ИИ ×», если значение из справочных данных ИИ. */
  mark: ReactNode;
  onChange: (v: string) => void;
}) {
  return (
    <td>
      <CellInput
        value={value}
        inputMode="decimal"
        aria-label={label}
        $invalid={eqNumError(field, value) !== null}
        onChange={(e) => onChange(e.target.value)}
      />
      {mark}
    </td>
  );
}

const OTHER = '__other__';

/** Способ мобилизации: типовые варианты + свободный ввод («другое…»). */
function MethodCell({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const preset = MOBILIZATION_METHODS.includes(value);
  const [custom, setCustom] = useState(!preset && value !== '');
  const selectValue = preset ? value : custom ? OTHER : '';
  return (
    <>
      <CellSelect
        value={selectValue}
        aria-label="Способ мобилизации"
        onChange={(e) => {
          const v = e.target.value;
          if (v === OTHER) {
            setCustom(true);
            onChange('');
          } else {
            setCustom(false);
            onChange(v);
          }
        }}
      >
        <option value="">—</option>
        {MOBILIZATION_METHODS.map((m) => (
          <option key={m} value={m}>
            {m}
          </option>
        ))}
        <option value={OTHER}>другое…</option>
      </CellSelect>
      {selectValue === OTHER && (
        <CellInput
          value={value}
          aria-label="Способ мобилизации (свой вариант)"
          placeholder="как доставляется"
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </>
  );
}

/**
 * Поле наименования с подсказками из справочника. Список рисуется в портале с
 * `position: fixed`: таблица техники прокручивается по горизонтали и обрезала
 * бы выпадающий список.
 */
function NameInput({
  value,
  registry,
  invalid = false,
  onChange,
  onPick,
}: {
  value: string;
  registry: PtoEquipment[];
  /** Наименование длиннее, чем принимает сервер. */
  invalid?: boolean;
  onChange: (v: string) => void;
  onPick: (e: PtoEquipment) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [hl, setHl] = useState(0);
  const [rect, setRect] = useState<{ top: number; left: number; width: number } | null>(null);

  const q = value.trim().toLowerCase();
  const options = useMemo(
    () =>
      registry
        .filter(
          (e) =>
            !q ||
            e.name.toLowerCase().includes(q) ||
            (e.characteristics ?? '').toLowerCase().includes(q),
        )
        .slice(0, 8),
    [registry, q],
  );

  const place = useCallback(() => {
    const r = ref.current?.getBoundingClientRect();
    if (r) setRect({ top: r.bottom + 2, left: r.left, width: Math.max(r.width, 340) });
  }, []);

  useEffect(() => {
    if (!open) return;
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, place]);

  const shown = open && options.length > 0 && rect !== null;

  function close() {
    setOpen(false);
    setRect(null);
  }

  function pick(e: PtoEquipment) {
    onPick(e);
    close();
  }

  return (
    <>
      <CellInput
        ref={ref}
        value={value}
        $invalid={invalid}
        autoComplete="off"
        role="combobox"
        aria-label="Наименование и марка"
        aria-autocomplete="list"
        aria-expanded={shown}
        placeholder={registry.length > 0 ? 'начните вводить или выберите' : 'Автокран КС-55732'}
        onFocus={() => setOpen(true)}
        onBlur={close}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
          setHl(0);
        }}
        onKeyDown={(e) => {
          if (!shown) {
            if (e.key === 'ArrowDown') setOpen(true);
            return;
          }
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHl((h) => Math.min(h + 1, options.length - 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHl((h) => Math.max(h - 1, 0));
          } else if (e.key === 'Enter') {
            e.preventDefault();
            pick(options[Math.min(hl, options.length - 1)]);
          } else if (e.key === 'Escape') {
            // Только список: Escape не должен закрывать весь мастер.
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
            close();
          }
        }}
      />
      {shown &&
        createPortal(
          <Suggest $top={rect.top} $left={rect.left} $width={rect.width} role="listbox">
            {options.map((e, k) => (
              <SuggestItem
                key={e.id}
                role="option"
                aria-selected={k === hl}
                $active={k === hl}
                onMouseEnter={() => setHl(k)}
                // mousedown, а не click: иначе поле теряет фокус и список закрывается раньше выбора.
                onMouseDown={(ev) => {
                  ev.preventDefault();
                  pick(e);
                }}
              >
                {e.name}
                <small>{equipmentSummary(e)}</small>
              </SuggestItem>
            ))}
          </Suggest>,
          document.body,
        )}
    </>
  );
}
